import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Sparkles, ShieldCheck, Zap, Users2 } from 'lucide-react';
import { SectionHeader } from './HowItWorksSection';

const differentiators = [
  {
    icon: Brain,
    title: 'A pessoa certa pra sua ideia',
    desc: 'A gente entende seu projeto e mostra para os investidores que mais combinam com ele. Você não precisa procurar — eles aparecem.',
    size: 'lg',
    color: 'blue',
    accent: 'Conexões inteligentes',
  },
  {
    icon: Sparkles,
    title: 'Encontramos quem combina com você',
    desc: 'Mostramos investidores que acreditam em ideias parecidas com a sua.',
    size: 'md',
    color: 'gold',
  },
  {
    icon: ShieldCheck,
    title: 'Mais confiança pra todo mundo',
    desc: 'Cada perfil é verificado. Você conversa só com pessoas reais e interessadas.',
    size: 'md',
    color: 'blue',
  },
  {
    icon: Zap,
    title: 'Converse direto, sem rodeios',
    desc: 'Quando rolar interesse, é só abrir o chat e bater um papo. Simples assim.',
    size: 'md',
    color: 'gold',
  },
  {
    icon: Users2,
    title: 'Uma comunidade que torce por você',
    desc: 'Aqui você encontra gente apaixonada por boas ideias, dispostas a ajudar.',
    size: 'md',
    color: 'blue',
  },
];

const DifferentiatorsSection = () => {
  return (
    <section id="differentiators" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          eyebrow="Por que aqui"
          title={<>Feito pra ser <span className="gradient-text">simples e humano</span>.</>}
          subtitle="Sem complicação, sem termos difíceis. Só pessoas se conectando e fazendo bons negócios acontecerem."
        />

        <div className="mt-16 grid lg:grid-cols-3 gap-5 lg:gap-6">
          {/* Large card */}
          <BigCard d={differentiators[0]} />

          {/* Right column - 2 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5 lg:gap-6">
            <SmallCard d={differentiators[1]} delay={0.15} />
            <SmallCard d={differentiators[2]} delay={0.25} />
          </div>

          {/* Third column - 2 cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-5 lg:gap-6">
            <SmallCard d={differentiators[3]} delay={0.35} />
            <SmallCard d={differentiators[4]} delay={0.45} />
          </div>
        </div>
      </div>
    </section>
  );
};

const BigCard = ({ d }) => {
  const Icon = d.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7 }}
      whileHover={{ y: -8 }}
      className="relative spotlight-card glass-card-blue rounded-3xl p-7 lg:p-9 overflow-hidden card-hover h-full min-h-[440px] flex flex-col"
    >
      {/* Background grid */}
      <div className="absolute inset-0 grid-pattern opacity-50" />
      {/* Background glow */}
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#4facfe]/20 rounded-full blur-3xl" />

      <div className="relative z-10 flex flex-col h-full">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#4facfe]/30 to-[#1e90ff]/15 text-[#4facfe] flex items-center justify-center mb-6 glow-blue">
          <Icon size={26} />
        </div>

        <h3 className="text-2xl lg:text-3xl heading-display font-bold text-white">{d.title}</h3>
        <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-md">{d.desc}</p>

        {/* Visual: neural network */}
        <div className="mt-auto pt-8 relative">
          <svg viewBox="0 0 300 120" className="w-full h-32">
            <defs>
              <linearGradient id="nn-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#4facfe" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#ffa726" stopOpacity="0.8" />
              </linearGradient>
            </defs>
            {/* Connection lines */}
            {[20, 60, 100].map((y1) =>
              [40, 80].map((y2) =>
                [180, 240].map((x2) => (
                  <line key={`${y1}-${y2}-${x2}`} x1="40" y1={y1} x2={x2} y2={y2} stroke="url(#nn-line)" strokeOpacity="0.3" strokeWidth="0.6" />
                ))
              )
            )}
            {/* Layer 1 */}
            {[20, 60, 100].map((y, i) => (
              <circle key={`l1-${i}`} cx="40" cy={y} r="4" fill="#4facfe">
                <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
              </circle>
            ))}
            {/* Layer 2 */}
            {[40, 80].map((y, i) => (
              <circle key={`l2-${i}`} cx="180" cy={y} r="5" fill="#ffffff">
                <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" begin={`${0.5 + i * 0.3}s`} repeatCount="indefinite" />
              </circle>
            ))}
            {/* Layer 3 */}
            {[60].map((y, i) => (
              <circle key={`l3-${i}`} cx="240" cy={y} r="6" fill="#ffa726">
                <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" begin="1s" repeatCount="indefinite" />
              </circle>
            ))}
          </svg>
          <div className="absolute bottom-0 right-0 text-[10px] uppercase tracking-widest text-[#4facfe]/70">Conexões reais, todo dia</div>
        </div>
      </div>
    </motion.div>
  );
};

const SmallCard = ({ d, delay }) => {
  const Icon = d.icon;
  const isGold = d.color === 'gold';
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -6 }}
      className={`relative spotlight-card rounded-3xl p-6 card-hover overflow-hidden ${
        isGold ? 'glass-card-gold' : 'glass-card'
      }`}
    >
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl ${
        isGold ? 'bg-[#ffa726]/15' : 'bg-[#4facfe]/15'
      }`} />
      <div className="relative z-10">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
          isGold
            ? 'bg-gradient-to-br from-[#ffa726]/25 to-[#ff7043]/10 text-[#ffd54f]'
            : 'bg-gradient-to-br from-[#4facfe]/25 to-[#1e90ff]/10 text-[#4facfe]'
        }`}>
          <Icon size={20} />
        </div>
        <h3 className="text-lg font-semibold text-white">{d.title}</h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed">{d.desc}</p>
      </div>
    </motion.div>
  );
};

export default DifferentiatorsSection;
